/**
 * Generic filter removal utility for URL-synchronized filter management
 *
 * This function provides a reusable pattern for removing filters from URL parameters
 * and updating the state accordingly. It handles both array-based filters (where
 * specific values can be removed) and single-value filters.
 */

export type FilterParamValue = string | number | boolean | (string | number)[] | undefined;
export type FilterParamsRecord = Record<string, FilterParamValue>;

export interface FilterRemovalConfig<TFilters extends object> {
    /**
     * Function to convert URL parameters to the filter format
     */
    convertUrlParamsToFilters: (urlParams: FilterParamsRecord) => TFilters;

    /**
     * Function to apply the updated filters (typically updates URL and state)
     */
    applyFilters: (filters: TFilters) => void;

    /**
     * Current URL parameters
     */
    currentParams: FilterParamsRecord;
}

/**
 * Creates a handleRemoveFilter function for a specific filter type
 *
 * @param config Configuration object with converter and apply functions
 * @returns A handleRemoveFilter function specialized for the given filter type
 */
export function createFilterRemover<TFilters extends object>(config: FilterRemovalConfig<TFilters>) {
    return function handleRemoveFilter(key: keyof TFilters, value?: string | number): void {
        // Work with current URL params for more reliable state
        const currentFilters = config.convertUrlParamsToFilters(config.currentParams);

        if (value !== undefined && Array.isArray(currentFilters[key])) {
            // Remove specific value from array
            const array = currentFilters[key] as (string | number)[];
            const newArray = array.filter(item => item !== value);
            if (newArray.length === 0) {
                delete currentFilters[key];
            } else {
                const updated = { ...currentFilters, [key]: newArray };
                config.applyFilters(updated as TFilters);
                return;
            }
        } else {
            // Remove entire filter (for single values or when no specific value provided)
            delete currentFilters[key];
        }

        // Apply the updated filters directly (this will update both URL and local state)
        config.applyFilters(currentFilters);
    };
}

/**
 * Generic URL parameter to filters converter
 *
 * Converts URL search parameters to a typed filter object, properly handling
 * boolean values and excluding pagination-related parameters.
 *
 * @param urlParams URL parameters object
 * @param excludeKeys Keys to exclude from conversion (defaults to pagination keys)
 * @returns Typed filter object
 */
export function convertUrlParamsToFilters<TFilters extends object>(urlParams: FilterParamsRecord, excludeKeys: string[] = ['page', 'size']): TFilters {
    const filters = {} as TFilters;

    for (const [key, value] of Object.entries(urlParams)) {
        if (!excludeKeys.includes(key) && value !== undefined && value !== null) {
            // Properly convert boolean values
            if (value === 'true') {
                (filters as FilterParamsRecord)[key] = true;
            } else if (value === 'false') {
                (filters as FilterParamsRecord)[key] = false;
            } else {
                (filters as FilterParamsRecord)[key] = value as FilterParamValue;
            }
        }
    }

    return filters;
}

/**
 * Removes empty/null/undefined values from a params object for API requests.
 * Accepts any object-like params (e.g. IMfhFilter) since it only iterates over entries.
 */
export function cleanFilterParamsObject<T extends object>(params?: T): Partial<Record<string, FilterParamValue>> {
    if (!params || typeof params !== 'object') {
        return {};
    }

    return Object.fromEntries(
        Object.entries(params).filter(
            ([_, value]) => value !== undefined && value !== null && value !== '' && value !== 'all' && !(Array.isArray(value) && value.length === 0),
        ),
    ) as Partial<Record<string, FilterParamValue>>;
}
